// The locked tablet stage (16.83).
//
// The HUD is laid out in absolute pixels against the viewport -- the Fleet
// Health Box is 200px wide at left:14px, the Scanner column 170px at
// right:14px, the Module Rack's circles are 64px, and the .over-expedition
// pane insets (230/200/46/220) are hand-derived from exactly those numbers
// (Sections 4a/4b, all LOCKED). That is a deliberate design, not an
// oversight: the panels are sized to a finger, and a finger does not get
// bigger when the screen does.
//
// The consequence is that the layout is only correct at the size it was
// designed for. On desktop that is fine -- the captain can size the window,
// and a fluid layout is what a desktop browser is for. On a tablet the
// captain cannot: the viewport is whatever the device and its browser chrome
// say it is, and at 1024x768 the fixed chrome eats a far larger share of the
// screen than it was ever meant to.
//
// So: on a touch device the game locks to one design stage and scales it
// uniformly to fit. On desktop nothing here applies at all and the layout
// stays fluid, exactly as it has always been (Don's direction).
//
// Stage size derivation (per CLAUDE.md: backward from intent, not invented):
//   * The HUD's own locked chrome budget is 430px horizontal (230 left for
//     the Fleet Health Box gutter + 200 right for the Scanner column) and
//     266px vertical (46 top + 220 bottom). Whatever the stage is, the
//     central viewport gets what's left.
//   * Real tablet landscape logical resolutions cluster tightly: iPad
//     10.9"/Air 1180x820, iPad Pro 11" 1194x834, Android 10" 1280x800, iPad
//     Pro 12.9" 1366x1024. 1280x800 sits at the top of that cluster and is
//     the exact Android 10" size, so the fit scale across the whole set
//     lands between 0.92 and 1.07 -- close enough to 1:1 that no text is
//     visibly softened in either direction.
//   * At 1280x800 the chrome budget leaves 850x534 for the actual game --
//     66% of the width and 67% of the height still belongs to space, which
//     is the ratio the mockups were tuned at.
// 16:10, therefore, and landscape (see the portrait note below).
export const STAGE_W = 1280;
export const STAGE_H = 800;

// "Touch device", not "small screen". A desktop with a touchscreen still
// reports hover:hover because it also has a mouse, so it correctly stays on
// the fluid layout; a tablet reports neither hover nor a fine pointer. This
// is the standard test and it is the one that matches Don's rule -- the
// trigger is the kind of device, not the number of pixels.
export const TOUCH_DEVICE_QUERY = "(hover: none) and (pointer: coarse)";
export const PORTRAIT_QUERY = "(orientation: portrait)";

export interface StageFit {
  /** Uniform scale factor -- never separate x/y, so nothing is distorted. */
  scale: number;
  /** Letterbox offsets that center the scaled stage in the viewport. */
  offsetX: number;
  offsetY: number;
}

/**
 * Fit the stage into a viewport: scale to the smaller axis ratio and center.
 * Pure, so the math is testable without a DOM.
 */
export function fitStage(
  viewportW: number,
  viewportH: number,
  stageW: number = STAGE_W,
  stageH: number = STAGE_H
): StageFit {
  // A zero or NaN viewport is a real thing that happens mid-orientation-change
  // on tablet hardware. Falling back to 1:1 rather than emitting scale 0
  // matters because a scale of 0 collapses the stage to nothing, and if the
  // follow-up resize event is coalesced away it would never come back.
  if (!(viewportW > 0) || !(viewportH > 0)) {
    return { scale: 1, offsetX: 0, offsetY: 0 };
  }
  const scale = Math.min(viewportW / stageW, viewportH / stageH);
  // Offsets rounded to whole pixels -- a fractional translate on top of a
  // fractional scale is what makes 10px HUD labels look smeared.
  return {
    scale,
    offsetX: Math.round((viewportW - stageW * scale) / 2),
    offsetY: Math.round((viewportH - stageH * scale) / 2)
  };
}

/**
 * Every custom property the stage CSS reads. The stage's own dimensions are
 * published from here too, rather than being written literally in the
 * stylesheet: the CSS needs them to derive --sf-vh/--sf-vw, and two copies
 * of a layout constant is exactly how they drift (16.82's own note).
 */
export function stageCssVars(fit: StageFit, stageW: number = STAGE_W, stageH: number = STAGE_H): Record<string, string> {
  return {
    "--sf-stage-w": `${stageW}px`,
    "--sf-stage-h": `${stageH}px`,
    "--sf-stage-scale": `${fit.scale}`,
    "--sf-stage-x": `${fit.offsetX}px`,
    "--sf-stage-y": `${fit.offsetY}px`
  };
}

// The minimum surface of <html> this module touches -- narrow on purpose so
// the applier can be unit-tested with a plain object instead of needing jsdom
// (this repo's vitest runs with no DOM environment at all).
export interface StageHost {
  classList: { toggle(token: string, force: boolean): void };
  style: { setProperty(property: string, value: string): void };
}

export const STAGE_LOCKED_CLASS = "sf-stage-locked";
export const STAGE_PORTRAIT_CLASS = "sf-stage-portrait";

export interface StageState {
  /** Is this a touch device (i.e. should the stage lock at all)? */
  locked: boolean;
  /** Is a locked stage currently being asked to live in a portrait viewport? */
  portrait: boolean;
  viewportW: number;
  viewportH: number;
}

/**
 * Write one stage state onto a host element. Idempotent: calling it twice with
 * the same state is a no-op in effect, which is what lets it be wired to
 * resize, orientationchange and two media queries at once without debouncing.
 */
export function applyStageState(host: StageHost, stateIn: StageState): StageFit {
  const fit = fitStage(stateIn.viewportW, stateIn.viewportH);
  host.classList.toggle(STAGE_LOCKED_CLASS, stateIn.locked);
  // Portrait only means anything while locked -- a desktop browser window
  // that happens to be taller than it is wide is a perfectly ordinary
  // fluid-layout window, not a tablet held the wrong way.
  host.classList.toggle(STAGE_PORTRAIT_CLASS, stateIn.locked && stateIn.portrait);
  const vars = stageCssVars(fit);
  for (const [name, value] of Object.entries(vars)) {
    host.style.setProperty(name, value);
  }
  return fit;
}

// Structurally typed (not HTMLCanvasElement / PointerEvent) so the conversion
// below is unit-testable in a DOM-free test run.
export interface CanvasLike {
  width: number;
  height: number;
  getBoundingClientRect(): { left: number; top: number; width: number; height: number };
}
export interface PointerLike {
  clientX: number;
  clientY: number;
}

/**
 * Convert a pointer event into canvas backing-store coordinates (16.83).
 *
 * The Expedition View sizes its canvases with `canvas.width = clientWidth`,
 * which is *layout* size and therefore ignores any CSS transform on an
 * ancestor. `getBoundingClientRect()` does the opposite: it reports the
 * *visual* box, transform included. Inside the locked stage those two differ
 * by the stage scale, so the old `ev.clientX - rect.left` was off by exactly
 * that factor -- taps would land short of where the captain touched, further
 * off the further from the top-left they aimed.
 *
 * Dividing through by rect.width/height fixes it and is scale-agnostic: on
 * desktop the ratio is 1 and this is identical to the arithmetic it replaces.
 */
export function canvasPointFromEvent(canvas: CanvasLike, ev: PointerLike): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  // A canvas with a zero-width visual box (display:none, or measured before
  // layout) would divide by zero -- fall back to the untransformed reading
  // rather than returning NaN, which would poison every downstream hit test.
  const sx = rect.width > 0 ? canvas.width / rect.width : 1;
  const sy = rect.height > 0 ? canvas.height / rect.height : 1;
  return { x: (ev.clientX - rect.left) * sx, y: (ev.clientY - rect.top) * sy };
}

// The portrait interstitial. A locked landscape stage cannot honestly be
// shown in portrait: fitting 1280x800 into an 820x1180 viewport gives a scale
// of 0.64, which renders the 10px HUD labels at 6.4px inside a 668px band of
// dead space. Asking the captain to rotate is the honest answer, and it also
// resolves the one sub-question 16.49 deliberately preserved when it closed
// the UI-dock-side item ("the Scanner column would plausibly read better
// docked bottom on a tablet held in portrait") -- there is no portrait HUD to
// re-dock, because portrait is not a layout the game offers.
const ROTATE_NOTICE_CLASS = "sf-rotate-notice";

function ensureRotateNotice(doc: Document): void {
  if (doc.querySelector(`.${ROTATE_NOTICE_CLASS}`)) return;
  const notice = doc.createElement("div");
  notice.className = ROTATE_NOTICE_CLASS;
  notice.innerHTML =
    '<div class="sf-rotate-inner">' +
    '<div class="sf-rotate-glyph" aria-hidden="true">⟲</div>' +
    "<div><strong>Rotate to landscape</strong></div>" +
    "<p>Starforge's bridge is a landscape console — the Fleet Health Box, Scanner column and Module Rack need the width.</p>" +
    "</div>";
  // Appended to <body>, deliberately outside #app: it must cover the whole
  // real screen including the letterbox bands, and it must not itself be
  // scaled by the stage transform it is standing in for.
  doc.body.appendChild(notice);
}

/**
 * Wire the stage to the live window. Returns a disposer (used by tests and
 * anything that tears the app down; the app itself never calls it).
 */
export function installResponsiveStage(win: Window): () => void {
  const doc = win.document;
  const host = doc.documentElement as unknown as StageHost;
  ensureRotateNotice(doc);

  // matchMedia is guarded rather than assumed: this runs at module-init time
  // on whatever browser the captain has, and a missing matchMedia should
  // degrade to "desktop, fluid layout" instead of throwing on boot.
  const touchMq = typeof win.matchMedia === "function" ? win.matchMedia(TOUCH_DEVICE_QUERY) : null;
  const portraitMq = typeof win.matchMedia === "function" ? win.matchMedia(PORTRAIT_QUERY) : null;

  const update = (): void => {
    applyStageState(host, {
      locked: touchMq?.matches ?? false,
      portrait: portraitMq?.matches ?? false,
      viewportW: win.innerWidth,
      viewportH: win.innerHeight
    });
  };

  update();

  win.addEventListener("resize", update);
  // orientationchange as well as resize: on iOS the resize that follows a
  // rotation has historically fired before the new innerWidth/innerHeight are
  // readable, and one extra idempotent update is cheaper than being wrong.
  win.addEventListener("orientationchange", update);
  touchMq?.addEventListener?.("change", update);
  portraitMq?.addEventListener?.("change", update);

  return () => {
    win.removeEventListener("resize", update);
    win.removeEventListener("orientationchange", update);
    touchMq?.removeEventListener?.("change", update);
    portraitMq?.removeEventListener?.("change", update);
  };
}
